import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Tiny SVG sparkline of efficiency % over the past N days.
 *
 * Sits next to the headline percentage on the Trust Centre. Renders
 * nothing if the API returns fewer than 7 points or if every point is 0
 * (no story to tell yet).
 */
export function EfficiencySparkline({ days = 30 }) {
  const [points, setPoints] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/public/trust-savings/history?days=${days}`)
      .then(({ data }) => {
        if (!cancelled) setPoints(data?.points || []);
      })
      .catch(() => {
        if (!cancelled) setPoints([]);
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  if (!points || points.length < 7) return null;
  const allZero = points.every((p) => (p.efficiency_pct || 0) === 0);
  if (allZero) return null;

  // SVG geometry — 120×34 nominal, scales with the parent.
  const W = 120;
  const H = 34;
  const PAD_Y = 4;
  const max = 100;
  const min = 0;
  const xStep = W / (points.length - 1);
  const ys = points.map(
    (p) =>
      H - PAD_Y -
      ((Math.max(min, Math.min(max, p.efficiency_pct)) - min) / (max - min)) *
        (H - PAD_Y * 2),
  );
  const path = points
    .map((_, i) => `${i === 0 ? "M" : "L"} ${(i * xStep).toFixed(1)} ${ys[i].toFixed(1)}`)
    .join(" ");
  const last = points[points.length - 1];
  const first = points[0];
  const delta = (last.efficiency_pct || 0) - (first.efficiency_pct || 0);
  // Trend colour: up = emerald, flat = slate, down = rose.
  const stroke =
    delta > 1.5 ? "#10b981" : delta < -1.5 ? "#f43f5e" : "#94a3b8";
  const trendLabel =
    delta > 1.5
      ? `+${delta.toFixed(0)} pts vs ${days}d ago`
      : delta < -1.5
        ? `${delta.toFixed(0)} pts vs ${days}d ago`
        : `Flat over ${days}d`;

  return (
    <div
      className="flex items-center gap-2"
      data-testid="trust-efficiency-sparkline"
      title={trendLabel}
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-7 w-24 shrink-0"
        preserveAspectRatio="none"
        aria-label={`Efficiency trend, ${trendLabel}`}
      >
        {/* baseline */}
        <line
          x1={0}
          x2={W}
          y1={H - PAD_Y}
          y2={H - PAD_Y}
          stroke="#e2e8f0"
          strokeWidth={0.5}
        />
        <path
          d={path}
          fill="none"
          stroke={stroke}
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* end marker */}
        <circle
          cx={(points.length - 1) * xStep}
          cy={ys[ys.length - 1]}
          r={2}
          fill={stroke}
        />
      </svg>
      <span
        className="text-[10px] font-medium text-ek-ink/60"
        data-testid="trust-efficiency-trend"
        style={{ color: stroke }}
      >
        {trendLabel}
      </span>
    </div>
  );
}
